import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLearning } from '../../context/LearningContext';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import './StatsPanel.css';

const BLOOM_COLORS = {
  remember: '#4ade80', understand: '#60a5fa', apply: '#f59e0b',
  analyze: '#f97316', evaluate: '#ef4444', create: '#a855f7',
};

export default function StatsPanel() {
  const { t } = useTranslation();
  const { fetchStats } = useLearning();
  const [stats, setStats] = useState(null);
  const [period, setPeriod] = useState('7d');

  useEffect(() => {
    fetchStats(period).then(setStats).catch(console.error);
  }, [period, fetchStats]);

  if (!stats) return <div className="stats-loading">{t('learn.loadingStats')}</div>;

  const bloomData = (stats.bloomMastery || []).map(b => ({
    name: t(`learn.bloomLevels.${b.bloom_level}`),
    mastery: Math.round((b.mastery || 0) * 100),
    level: b.bloom_level,
  }));

  return (
    <div className="stats-panel">
      <div className="stats-header">
        <h1>{t('learn.stats')}</h1>
        <div className="period-selector">
          {['7d', '30d', 'all'].map(p => (
            <button
              key={p}
              className={`period-btn ${period === p ? 'active' : ''}`}
              onClick={() => setPeriod(p)}
            >
              {p === 'all' ? t('learn.periodAll') : p}
            </button>
          ))}
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <span className="stat-value">{Math.round((stats.retention || 0) * 100)}%</span>
          <span className="stat-label">{t('learn.retention')}</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{stats.streak}</span>
          <span className="stat-label">{t('learn.streak')}</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{stats.reviewed}</span>
          <span className="stat-label">{t('learn.totalLearned')}</span>
        </div>
      </div>

      {stats.heatmap?.length > 0 && (
        <div className="chart-section">
          <h3>{t('learn.reviewsPerDay')}</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={stats.heatmap}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" tickFormatter={d => new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="cnt" fill="var(--color-primary, #60a5fa)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {bloomData.length > 0 && (
        <div className="chart-section">
          <h3>{t('learn.bloomMastery')}</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={bloomData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" domain={[0, 100]} unit="%" />
              <YAxis type="category" dataKey="name" width={100} />
              <Tooltip />
              <Bar dataKey="mastery">
                {bloomData.map((entry) => (
                  <Cell key={entry.level} fill={BLOOM_COLORS[entry.level] || '#888'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
